import React, {
  Dispatch, ReactElement, SetStateAction, useState,
} from 'react';
import {
  Pressable, StyleSheet, Text, View,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import TemplateIcon from '../40pxIcons/TemplateIcon';
import EllipsisIcon from '../EllipsisIcon';
import AlertModal from '../AlertModal';
import AddTemplateModal from './AddTemplateModal';
import deleteTemplate from '../../requests/Assets/DeleteTemplate';
import { logEvent } from '../LogEvent';
import Style from '../../constants/Style';
import {
  Template, TemplateGroup, TemplateType, TemplateTypeTitles, UserPermission,
} from '../../constants/Types';
import SensorIcon24 from '../24pxIcons/SensorIcon24';
import MeterIcon from '../24pxIcons/MeterIcon';
import { DELETE_TEMPLATE_FAILURE } from '../../constants/AlertMessages';

interface TemplateGroupRowMobileProps {
  setOptionsSelected: Dispatch<SetStateAction<boolean>>,
  showParentAlert,
  deleteAction,
  template: Template,
  setLinkAssetsView: () => void,
  setSelectedId: () => void,
  templateGroups: TemplateGroup[],
  update,
}

export default function TemplateGroupRowMobile({
  setOptionsSelected, showParentAlert, deleteAction, template, setLinkAssetsView, setSelectedId,
  templateGroups, update,
}: TemplateGroupRowMobileProps) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canUpdateTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const [loading, setLoading] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const deleteError = (text?: string) => showAlert(
    'Error',
    text ?? DELETE_TEMPLATE_FAILURE,
  );

  const removeTemplate: () => void = () => {
    deleteAction(template.id);
    setOptionsOpen(false);
  };

  const attemptDeletion = () => {
    setOptionsSelected(null);
    setAlertVisible(false);
    setLoading(true);

    deleteTemplate(template.id).then((res: any) => {
      if (res && !res.isError) {
        showAlert(
          'Template successfully deleted',
          <Text>
            <Text style={Style.alertBoldText}>{template.name}</Text>
            {' was successfully deleted'}
          </Text>,
          false,
          removeTemplate,
        );
      } else deleteError(res?.errorText);
    }).catch((error) => deleteError(error?.message))
      .finally(() => setLoading(false));
  };

  const renderIcon = (type: TemplateType) => {
    if (type === TemplateType.SENSOR) return <SensorIcon24 />;
    if (type === TemplateType.METER) return <MeterIcon stroke={'#fff'} />;
    return <TemplateIcon />;
  };

  return (
    <>
      <Pressable
        style={(state) => [{ opacity: state.pressed ? 0.3 : 1 }, styles.container]}
        onPress={() => {
          logEvent('TemplateGroupRowMobile', 'template_group_row');
          setLinkAssetsView();
          setSelectedId();
        }}
      >
        <View style={styles.icon}>
          {renderIcon(template.type)}
        </View>
        <View>
          <Text style={styles.title}>{template.name}</Text>
          <Text style={styles.type}>
            {TemplateTypeTitles[template.type] ?? TemplateTypeTitles[TemplateType.ASSET]}
          </Text>
        </View>
        <View style={{ flexDirection: 'row', flex: 1 }} />
        {(canUpdateTemplates || canDeleteTemplates) ? <Pressable
          style={(state) => [{
            opacity: state.pressed ? 0.3 : 1,
            position: 'absolute',
            right: 12,
            top: 20,
          }]}
          hitSlop={{
            top: 25,
            bottom: 25,
            left: 15,
            right: 15,
          }}
          onPress={() => setOptionsOpen(true)}
        >
          <EllipsisIcon fill={Colors.light.hereworksBlue400} />
        </Pressable> : null}
      </Pressable>
      <Modal
        style={{ margin: 0 }}
        isVisible={optionsOpen}
        onBackdropPress={() => setOptionsOpen(false)}
        onBackButtonPress={() => setOptionsOpen(false)}
      >
        <View
          style={{
            position: 'absolute',
            width: '100%',
            bottom: 0,
            paddingBottom: 24,
            backgroundColor: '#fff',
            borderTopStartRadius: 12,
            borderTopEndRadius: 12,
            overflow: 'hidden',
          }}
        >
          {canUpdateTemplates && <Pressable
            style={(state) => [{ opacity: state.pressed ? 0.3 : 1 }, styles.contextItem]}
            onPress={() => {
              logEvent('TemplateGroupRowMobile', 'Edit');
              setEditModalVisible(true);
            }}
          >
            <Text style={styles.contextItemText}>{'Edit'}</Text>
          </Pressable>}
          {canDeleteTemplates && <Pressable
            style={(state) => [{ opacity: state.pressed ? 0.3 : 1 }, styles.contextItem]}
            onPress={() => {
              logEvent('TemplateGroupRowMobile', 'Delete');
              showAlert(
                'Delete template',
                <>
                  {'Are you sure you want to delete the template '}
                  <Text style={Style.alertBoldText}>{template.name}</Text>
                </>,
                true,
                attemptDeletion,
                'Delete',
              );
            }}
          >
            <Text style={styles.contextItemText}>{'Delete'}</Text>
          </Pressable>}
        </View>
        <AddTemplateModal
          templateGroups={templateGroups}
          isVisible={editModalVisible}
          hide={() => setEditModalVisible(false)}
          templateToUpdate={template}
          update={update}
          showParentAlert={showParentAlert}
        />
        <AlertModal
          modalVisible={alertVisible}
          title={alertTitle}
          message={alertMessage}
          error={alertError}
          action={alertAction}
          actionTitle={alertActionTitle}
          {...{ loading }}
          setModalVisible={setAlertVisible}
        />
      </Modal>
    </>
  );
}
const styles = StyleSheet.create({
  icon: {
    marginEnd: 12,
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  title: {
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 14,
    lineHeight: 21,
  },
  type: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  container: {
    paddingHorizontal: 18,
    paddingVertical: 19,
    borderRadius: 10,
    marginVertical: 2,
    marginHorizontal: 20,
    backgroundColor: Colors.light.hereworksWhite100,
    alignItems: 'center',
    flexDirection: 'row',
  },
  contextItem: {
    paddingVertical: 20,
    backgroundColor: '#fff',
    alignItems: 'flex-start',
  },
  contextItemText: {
    marginLeft: 14,
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 21,
  },
});
